import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { fetchUnreadCount } from "../services/notificationService";
import websocketService from "../services/websocket";

// Create the context
const NotificationContext = createContext();

// Create the provider component
export const NotificationProvider = ({ children }) => {
  const { user, apiCall, isAuthenticated, tokenRef } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationDot, setShowNotificationDot] = useState(false);

  // Function to manually clear notifications (used when marking all as read)
  const clearNotifications = useCallback(() => {
    setUnreadCount(0);
    setShowNotificationDot(false);
  }, []);

  // Fetch initial unread count
  const loadUnreadCount = useCallback(async () => {
    if (!user?.profile?.id) {
      return;
    }

    try {
      const result = await fetchUnreadCount(apiCall);
      const count = result.unreadCount || 0;
      setUnreadCount(count);
      setShowNotificationDot(count > 0);
    } catch (error) {
      // Error handling for unread count fetch
    }
  }, [apiCall, user?.profile?.id]);

  // Load unread count when user is available
  useEffect(() => {
    if (isAuthenticated && user?.profile?.id) {
      // Small delay to ensure tokenRef sync effect has run
      const timer = setTimeout(() => {
        if (tokenRef?.current) {
          loadUnreadCount();
        }
      }, 10); // 10ms to allow sync effects to complete
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.profile?.id, loadUnreadCount]);

  // Setup WebSocket listeners for real-time notifications
  useEffect(() => {
    if (!user?.profile?.id) {
      return;
    }

    const setupWebSocket = async () => {
      try {
        if (!websocketService.isConnected()) {
          return;
        }

        const socket = websocketService.getSocket();

        if (!socket?.connected) {
          return;
        }

        try {
          websocketService.unsubscribeFromProfile(user.profile.id);

          await new Promise((resolve) => setTimeout(resolve, 500));

          await websocketService.subscribeToProfile(user.profile.id);
        } catch (subError) {
          return;
        }

        // Remove any existing listeners first
        socket.off("new_notification");
        socket.off("notification_read");
        socket.off("all_notifications_read");

        // Listen for new notifications
        const handleNewNotification = (notification) => {
          setUnreadCount((prev) => prev + 1);
          setShowNotificationDot(true);
        };

        socket.on("new_notification", handleNewNotification);

        // Listen for notification read events
        socket.on("notification_read", () => {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        });

        // Listen for all notifications read
        socket.on("all_notifications_read", () => {
          setUnreadCount(0);
          setShowNotificationDot(false);
        });
      } catch (error) {
        // WebSocket setup error handling
      }
    };

    const timer = setTimeout(setupWebSocket, 1500);

    return () => {
      clearTimeout(timer);
      const socket = websocketService.getSocket();
      if (socket) {
        socket.off("new_notification");
        socket.off("notification_read");
        socket.off("all_notifications_read");
      }
    };
  }, [user?.profile?.id]);

  const value = {
    unreadCount,
    showNotificationDot,
    clearNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};